import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, ArrowUpRight, Building2, Loader2 } from "lucide-react";
import { fetchRealizations, photoUrl } from "../lib/api";
import { CATEGORIES, categoryLabel } from "../data/realizations";

const FILTERS = [{ key: "all", label: "Toutes" }, ...CATEGORIES];

function StatusPill({ status }) {
  const ongoing = /cours/i.test(status || "");
  return (
    <span
      data-testid="realization-status"
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${
        ongoing ? "bg-gold/90 text-navy" : "bg-emerald-500/90 text-white"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" /> {status || "Terminé"}
    </span>
  );
}

function ProjectCard({ p, i }) {
  const cover = p.cover || p.photos_after?.[0] || p.photos_during?.[0] || p.photos_before?.[0];
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    >
      <Link
        to={`/realisations/${p.id}`}
        data-testid={`realization-card-${p.id}`}
        className="group relative block overflow-hidden rounded-3xl border border-navy/5 bg-white shadow-[0_10px_40px_-18px_rgba(11,31,58,0.35)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-20px_rgba(11,31,58,0.45)]"
      >
        <div className="relative aspect-[4/3] overflow-hidden bg-navy/5">
          {cover ? (
            <img
              src={photoUrl(cover)}
              alt={p.title}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-navy/20"><Building2 size={48} /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-navy/10 to-transparent opacity-90" />
          <div className="absolute left-4 top-4 flex items-center gap-2">
            <span className="rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-navy backdrop-blur">
              {categoryLabel(p.category)}
            </span>
            {p.featured && (
              <span className="rounded-full bg-gold px-2.5 py-1 text-[11px] font-bold text-navy">★ Vedette</span>
            )}
          </div>
          <div className="absolute right-4 top-4"><StatusPill status={p.status} /></div>
          <div className="absolute inset-x-0 bottom-0 p-5">
            <h3 className="font-display text-xl font-bold text-white drop-shadow-sm">{p.title}</h3>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/80">
              {p.location && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {p.location}</span>}
              {p.year && <span className="inline-flex items-center gap-1"><Calendar size={13} /> {p.year}</span>}
            </div>
          </div>
          <span className="absolute bottom-5 right-5 grid h-10 w-10 translate-y-2 place-items-center rounded-full bg-gold text-navy opacity-0 shadow-lg transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
            <ArrowUpRight size={18} />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Realisations() {
  const [cat, setCat] = useState("all");
  const [list, setList] = useState(null);

  useEffect(() => {
    setList(null);
    fetchRealizations(cat).then(setList).catch(() => setList([]));
  }, [cat]);

  return (
    <main data-testid="realisations-page" className="bg-white">
      <title>Nos réalisations | TIRERA GROUP — Construction & BTP au Mali</title>
      <meta name="description" content="Découvrez les réalisations de TIRERA GROUP : construction de maisons, immeubles, rénovations et génie civil à Bamako et au Mali. Photos, descriptions et détails de chaque projet." />
      <meta property="og:title" content="Nos réalisations | TIRERA GROUP" />
      <meta property="og:description" content="Nos chantiers et projets de construction au Mali — maisons, immeubles, rénovations, génie civil." />
      <meta property="og:type" content="website" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-navy pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-gold/20 blur-3xl" />
        <div className="pointer-events-none absolute -left-24 bottom-0 h-72 w-72 rounded-full bg-gold/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <motion.span
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-sm font-medium text-gold-400"
          >
            <Building2 size={15} /> Portfolio TIRERA Construction & Habitat
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08 }}
            className="mt-5 max-w-3xl font-display text-4xl font-bold leading-[1.05] text-white sm:text-5xl lg:text-6xl"
          >
            Nos réalisations
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16 }}
            className="mt-4 max-w-2xl text-base text-white/70 sm:text-lg"
          >
            Des maisons clé en main aux immeubles, en passant par la rénovation et le génie civil — la preuve de notre savoir-faire, chantier après chantier.
          </motion.p>
        </div>
      </section>

      {/* Filtres */}
      <section className="sticky top-[70px] z-30 border-b border-navy/5 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-5 py-4 sm:px-8">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`realisation-filter-${f.key}`}
              onClick={() => setCat(f.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                cat === f.key ? "bg-navy text-white" : "bg-navy/5 text-navy/70 hover:bg-navy/10"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* Grille */}
      <section className="mx-auto max-w-7xl px-5 py-14 sm:px-8 sm:py-20">
        {!list ? (
          <div className="flex items-center justify-center py-24 text-navy/40"><Loader2 className="animate-spin" /></div>
        ) : list.length === 0 ? (
          <div data-testid="realisations-empty" className="rounded-3xl border border-dashed border-navy/15 bg-navy/[0.02] py-20 text-center">
            <Building2 className="mx-auto mb-4 text-navy/20" size={40} />
            <p className="text-navy/60">Aucune réalisation dans cette catégorie pour le moment.</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((p, i) => <ProjectCard key={p.id} p={p} i={i} />)}
          </div>
        )}
      </section>
    </main>
  );
}
